<template>
  <div
    class="mt-1"
    :class="inlineInputs ? 'grid gap-4 sm:grid-cols-2 items-start' : 'space-y-2'"
  >
    <div
      :class="
        ['once', 'weekly', 'monthly'].includes(doesRepeat) && inlineInputs
          ? 'sm:col-span-2'
          : ''
      "
    >
      <DropDownSelect
        v-model="doesRepeat"
        option-label="label"
        :label="$t('schedulerDescription')"
        option-value="value"
        :disabled="disabled"
        :options="intervalOptions"
        :placeholder="$t('wordsPleaseSelect')"
        @update:model-value="handleChange"
      />
    </div>
    <div v-if="doesRepeat === 'weekly'" class="flex flex-wrap gap-2">
      <div class="w-full">
        <DropDownSelectMultiple
          v-model="selectedWeekdays"
          :options="formattedDaysOfWeek"
          :label="$t('RepeatOn')"
          :placeholder="$t('wordsPleaseSelect')"
          @update:model-value="handleChange"
        />
      </div>
    </div>
    <div v-if="doesRepeat === 'monthly'" class="flex flex-wrap gap-2">
      <div class="w-full">
        <DropDownSelect
          v-model="monthlyValue"
          option-label="label"
          :label="$t('RepeatOn')"
          option-value="value"
          :options="monthlyArray"
          :placeholder="$t('wordsPleaseSelect')"
          @update:model-value="handleChange"
        />
      </div>
    </div>
    <div v-if="doesRepeat !== 'once'" class="flex flex-wrap gap-2">
      <div class="w-full">
        <MADateInput
          v-if="showEndDatePicker"
          v-model="endDate"
          :label="$t('EndsOn')"
          :disabled="disabled"
          class="w-full"
          format="yy-mm-dd"
          @update:model-value="handleChange"
        />
        <DropDownSelect
          v-if="showYearlyEndDateDropdown"
          v-model="yearlyEndDate"
          :label="$t('EndsOn')"
          option-label="label"
          option-value="value"
          :options="yearlyEndDateOptions"
          :placeholder="$t('wordsPleaseSelect')"
          @update:model-value="handleChange"
        />
      </div>
    </div>
    <!-- </div> -->
  </div>
</template>

<script setup lang="ts">
import type { CronData, ScheduleTime } from "@/types/task";
import DropDownSelect from "../shared/DropDownSelect.vue";
import DropDownSelectMultiple from "../shared/DropDownSelectMultiple.vue";
import { eachDayOfInterval, format, addYears } from "date-fns";
import MADateInput from "../primevueComponents/MADateInput.vue";

const props = defineProps<{
  value: string | CronData;
  type?: ScheduleTime["type"];
  disabled?: boolean;
  dateAndTime: Date;
  inlineInputs?: boolean;
}>();
const emit = defineEmits<{
  (e: "change", value: { value: CronData | string; type: ScheduleTime["type"] }): void;
}>();
const monthlyArray = [
  {
    label: `Start of month`,
    value: "startOfMonth",
  },
  {
    label: `Middle of month`,
    value: "middleOfMonth",
  },
  {
    label: `End of month`,
    value: "endOfMonth",
  },
];

const yearlyEndDateOptions = [
  {
    label: "One year",
    value: "oneYear",
  },
  {
    label: "Two years",
    value: "twoYears",
  },
  {
    label: "Three years",
    value: "threeYears",
  },
];

const intervalOptions = [
  {
    label: "Does not repeat",
    value: "once",
  },
  {
    label: "Daily",
    value: "daily",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
  {
    label: "Monthly",
    value: "monthly",
  },
  {
    label: "Annually",
    value: "yearly",
  },
];

// refs
const yearlyEndDate = ref(yearlyEndDateOptions[0].value);
const doesRepeat = ref(intervalOptions[0].value);
const endDate = ref(addYears(props.dateAndTime, 1));
const selectedWeekdays = ref<string[]>();
const monthlyValue = ref(monthlyArray[0].value || "");

const showYearlyEndDateDropdown = computed(() => doesRepeat.value === "yearly");
const showEndDatePicker = computed(
  () => doesRepeat.value !== "yearly" && doesRepeat.value !== "once"
);
const computedEndDate = computed(() => {
  if (!endDate.value) return format(addYears(props.dateAndTime, 1), "yyyy-MM-dd");
  return format(new Date(endDate.value), "yyyy-MM-dd");
});

const dateTime = computed(() => {
  return props.dateAndTime.toISOString();
});

const endYearDate = computed(() => {
  return props.dateAndTime;
});

const finalYearlyEndDateValue = computed(() => {
  if (!yearlyEndDate.value || !endYearDate.value) return "";
  switch (yearlyEndDate.value) {
    case "oneYear":
      return addYears(endYearDate.value, 1);
    case "twoYears":
      return addYears(endYearDate.value, 2);
    case "threeYears":
      return addYears(endYearDate.value, 3);
    default:
      return addYears(endYearDate.value, 1);
  }
});

const onceValue = computed((): CronData | "" => {
  if (!dateTime.value) return "";
  return {
    startDateTime: dateTime.value,
    interval: "once",
  };
});

const dailyValue = computed((): CronData => {
  return {
    startDateTime: dateTime.value,
    interval: "daily",
    endDate: computedEndDate.value,
  };
});

const weeklyValue = computed((): CronData | "" => {
  if (!selectedWeekdays.value?.length || !dateTime.value) return "";
  return {
    startDateTime: dateTime.value,
    interval: "weekly",
    repeatsOn: {
      type: "weekly",
      value: selectedWeekdays.value.join(", "),
    },
    endDate: computedEndDate.value,
  };
});

const finalMonthlyValue = computed((): CronData | "" => {
  if (!monthlyValue.value || !dateTime.value) return "";
  return {
    startDateTime: dateTime.value,
    interval: "monthly",
    repeatsOn: {
      type: "monthly",
      value: monthlyValue.value,
    },
    endDate: computedEndDate.value,
  };
});

const yearlyValue = computed((): CronData | "" => {
  if (!dateTime.value) return "";
  return {
    startDateTime: `${dateTime.value}`,
    interval: "yearly",
    endDate: `${finalYearlyEndDateValue.value}`,
  };
});

const today = new Date();

const allDaysOfWeek = eachDayOfInterval({
  start: today.setDate(today.getDate() - today.getDay()),
  end: today.setDate(today.getDate() - today.getDay() + 6),
});

const formattedDaysOfWeek = allDaysOfWeek.map(date => ({
  label: format(date, "EEEE"),
  value: format(date, "EEEE"),
}));

function handleChange() {
  if (!doesRepeat.value) return;
  switch (doesRepeat.value) {
    case "once":
      emit("change", {
        value: onceValue.value,
        type: "cron",
      });
      break;
    case "daily":
      emit("change", {
        value: dailyValue.value,
        type: "cron",
      });
      break;
    case "weekly":
      emit("change", {
        value: weeklyValue.value,
        type: "cron",
      });
      break;
    case "monthly":
      emit("change", {
        value: finalMonthlyValue.value,
        type: "cron",
      });
      break;
    case "yearly":
      emit("change", {
        value: yearlyValue.value,
        type: "cron",
      });
      break;
    default:
      emit("change", {
        value: onceValue.value,
        type: "cron",
      });
      break;
  }
}

onBeforeMount(() => {
  if (typeof props.value !== "string" && props.value.endDate) {
    console.log(props.value.endDate);
    endDate.value = new Date(props.value.endDate);
  }
});
</script>

<style>
button.vtd-datepicker-date {
  height: 1.6rem !important;
}
</style>
